import { useState, useEffect, useCallback } from 'react';
import { useAuthUser } from '../contexts/AuthUserContext';

export const useUserPreferences = () => {
    const user = useAuthUser();
    const [preferences, setPreferences] = useState({
        defaultPage: null,
        defaultDateRangeDays: 30, // Default to 30 days (1 month)
        // Add other preferences as needed
    });

    // Create a user-specific localStorage key
    const getStorageKey = useCallback(() => {
        if (!user?.email) return 'user_preferences_default';
        return `user_preferences_${user.email}`;
    }, [user?.email]);

    // Load preferences from localStorage
    useEffect(() => {
        if (!user?.email) return;

        try {
            const storageKey = getStorageKey();
            const savedPreferences = localStorage.getItem(storageKey);
            
            if (savedPreferences) {
                const parsed = JSON.parse(savedPreferences);
                // Migrate old 14-day default to 30 now that the backend supports 30 days
                if (parsed.defaultDateRangeDays === 14) {
                    parsed.defaultDateRangeDays = 30;
                    localStorage.setItem(storageKey, JSON.stringify(parsed));
                }
                setPreferences(prev => ({ ...prev, ...parsed }));
            }
        } catch (error) {
            console.error('Failed to load user preferences:', error);
        }
    }, [user?.email, getStorageKey]);

    // Save preferences to localStorage
    const savePreferences = useCallback((newPreferences) => {
        if (!user?.email) return;

        try {
            const storageKey = getStorageKey();
            const updatedPreferences = { ...preferences, ...newPreferences };
            
            localStorage.setItem(storageKey, JSON.stringify(updatedPreferences));
            setPreferences(updatedPreferences);
        } catch (error) {
            console.error('Failed to save user preferences:', error);
        }
    }, [preferences, user?.email, getStorageKey]);

    // Update specific preference
    const updatePreference = useCallback((key, value) => {
        savePreferences({ [key]: value });
    }, [savePreferences]);

    // Get default page preference
    const getDefaultPage = useCallback(() => {
        return preferences.defaultPage;
    }, [preferences.defaultPage]);

    // Set default page preference
    const setDefaultPage = useCallback((page) => {
        updatePreference('defaultPage', page);
    }, [updatePreference]);

    // Get default date range days preference
    const getDefaultDateRangeDays = useCallback(() => {
        return preferences.defaultDateRangeDays || 30;
    }, [preferences.defaultDateRangeDays]);

    // Set default date range days preference
    const setDefaultDateRangeDays = useCallback((days) => {
        const validDays = Math.min(Math.max(1, parseInt(days) || 30), 30);
        updatePreference('defaultDateRangeDays', validDays);
    }, [updatePreference]);

    return {
        preferences,
        savePreferences,
        updatePreference,
        getDefaultPage,
        setDefaultPage,
        getDefaultDateRangeDays,
        setDefaultDateRangeDays,
    };
}; 